import { useState } from "react";
import { useOrgSettings } from "./OrgSettingsContext";
import { computeDisplayHolidays } from "./types";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Clock,
  Globe,
  Calendar,
  Plus,
  Trash2,
  Loader2,
  Undo2,
  FolderTree,
} from "lucide-react";

export function TicketSettings() {
  const {
    formState,
    updateFormState,
    isLoadingHolidays,
    serverHolidays,
    pendingHolidayChanges,
    addPendingHoliday,
    removePendingHoliday,
    markHolidayForRemoval,
    unmarkHolidayForRemoval,
  } = useOrgSettings();

  // New holiday form state
  const [newHoliday, setNewHoliday] = useState({
    name: "",
    date: "",
    isRecurring: false,
    observeNearestWeekday: false,
  });

  const handleAddHoliday = () => {
    if (!newHoliday.name || !newHoliday.date) {
      return;
    }

    addPendingHoliday({
      name: newHoliday.name,
      date: newHoliday.date,
      isRecurring: newHoliday.isRecurring,
      observeNearestWeekday: newHoliday.observeNearestWeekday,
    });

    setNewHoliday({
      name: "",
      date: "",
      isRecurring: false,
      observeNearestWeekday: false,
    });
  };

  // Compute display list with pending changes
  const displayHolidays = computeDisplayHolidays(
    serverHolidays,
    pendingHolidayChanges
  );

  return (
    <div className="space-y-6">
      {/* Auto-Close Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Auto-Close Resolved Tickets
          </CardTitle>
          <CardDescription>
            Automatically close tickets after they've been resolved for a period
            of time.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1 flex-1">
              <Label htmlFor="autoCloseDays" className="font-medium">
                Days Until Auto-Close
              </Label>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Resolved tickets will automatically close after this many days
                of inactivity.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Input
                id="autoCloseDays"
                type="number"
                min={1}
                max={365}
                value={formState.autoCloseDays}
                className="w-20"
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10);
                  if (!isNaN(value) && value >= 1 && value <= 365) {
                    updateFormState({ autoCloseDays: value });
                  }
                }}
              />
              <span className="text-sm text-gray-500 dark:text-gray-400">
                days
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Categories */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FolderTree className="h-5 w-5" />
            Categories
          </CardTitle>
          <CardDescription>
            How tickets are categorized. Manage the categories themselves on
            the Categories tab.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1 flex-1">
              <Label htmlFor="requireCategoryOnResolve" className="font-medium">
                Require a category to resolve
              </Label>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Agents must choose a category before resolving a ticket.
                Customers resolving their own tickets, automation and
                auto-close aren't affected.
              </p>
            </div>
            <Switch
              id="requireCategoryOnResolve"
              checked={formState.requireCategoryOnResolve}
              onCheckedChange={(checked) =>
                updateFormState({ requireCategoryOnResolve: checked })
              }
            />
          </div>
        </CardContent>
      </Card>

      {/* Business Hours Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Business Hours
          </CardTitle>
          <CardDescription>
            Configure your organization's working hours for SLA calculations.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Timezone */}
          <div className="space-y-2">
            <Label htmlFor="timeZoneId">Timezone</Label>
            <Select
              value={formState.timeZoneId}
              onValueChange={(value) => updateFormState({ timeZoneId: value })}
            >
              <SelectTrigger className="w-full sm:w-80">
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="UTC">UTC</SelectItem>
                <SelectItem value="America/New_York">
                  Eastern Time (US)
                </SelectItem>
                <SelectItem value="America/Chicago">
                  Central Time (US)
                </SelectItem>
                <SelectItem value="America/Denver">
                  Mountain Time (US)
                </SelectItem>
                <SelectItem value="America/Los_Angeles">
                  Pacific Time (US)
                </SelectItem>
                <SelectItem value="Europe/London">London (UK)</SelectItem>
                <SelectItem value="Europe/Paris">
                  Central European Time
                </SelectItem>
                <SelectItem value="Europe/Berlin">Berlin (Germany)</SelectItem>
                <SelectItem value="Asia/Tokyo">Tokyo (Japan)</SelectItem>
                <SelectItem value="Asia/Shanghai">Shanghai (China)</SelectItem>
                <SelectItem value="Asia/Singapore">Singapore</SelectItem>
                <SelectItem value="Australia/Sydney">
                  Sydney (Australia)
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Working Hours */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="workDayStart">Work Day Start</Label>
              <Select
                value={formState.workDayStart}
                onValueChange={(value) =>
                  updateFormState({ workDayStart: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select start time" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 48 }, (_, i) => {
                    const hour = Math.floor(i / 2)
                      .toString()
                      .padStart(2, "0");
                    const minute = i % 2 === 0 ? "00" : "30";
                    const value = `${hour}:${minute}`;
                    return (
                      <SelectItem key={value} value={value}>
                        {value}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="workDayEnd">Work Day End</Label>
              <Select
                value={formState.workDayEnd}
                onValueChange={(value) =>
                  updateFormState({ workDayEnd: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select end time" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 48 }, (_, i) => {
                    const hour = Math.floor(i / 2)
                      .toString()
                      .padStart(2, "0");
                    const minute = i % 2 === 0 ? "00" : "30";
                    const value = `${hour}:${minute}`;
                    return (
                      <SelectItem key={value} value={value}>
                        {value}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Working Days */}
          <div className="space-y-3">
            <Label>Working Days</Label>
            <div className="flex flex-wrap gap-4">
              {[
                "Sunday",
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday",
              ].map((day) => (
                <div key={day} className="flex items-center space-x-2">
                  <Checkbox
                    id={`day-${day}`}
                    checked={formState.workingDays.includes(day)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        updateFormState({
                          workingDays: [...formState.workingDays, day],
                        });
                      } else {
                        updateFormState({
                          workingDays: formState.workingDays.filter(
                            (d) => d !== day
                          ),
                        });
                      }
                    }}
                  />
                  <Label
                    htmlFor={`day-${day}`}
                    className="text-sm font-normal cursor-pointer"
                  >
                    {day.slice(0, 3)}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Holidays */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Holidays
          </CardTitle>
          <CardDescription>
            Add holidays that will be excluded from SLA time calculations.
            Changes will be saved when you click "Save Changes".
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add Holiday Form */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              placeholder="Holiday name"
              maxLength={100} // BusinessHoliday.Name column limit; longer names fail the whole save
              value={newHoliday.name}
              onChange={(e) =>
                setNewHoliday({ ...newHoliday, name: e.target.value })
              }
              className="flex-1"
            />
            <Input
              type="date"
              value={newHoliday.date}
              onChange={(e) =>
                setNewHoliday({ ...newHoliday, date: e.target.value })
              }
              className="w-full sm:w-40"
            />
            <div className="flex items-center gap-2">
              <Checkbox
                id="isRecurring"
                checked={newHoliday.isRecurring}
                onCheckedChange={(checked) => {
                  const isRecurring = !!checked;
                  setNewHoliday({
                    ...newHoliday,
                    isRecurring,
                    observeNearestWeekday: isRecurring
                      ? newHoliday.observeNearestWeekday
                      : false,
                  });
                }}
              />
              <Label
                htmlFor="isRecurring"
                className="text-sm whitespace-nowrap cursor-pointer"
              >
                Yearly
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="observeNearestWeekday"
                checked={newHoliday.observeNearestWeekday}
                disabled={!newHoliday.isRecurring}
                onCheckedChange={(checked) =>
                  setNewHoliday({
                    ...newHoliday,
                    observeNearestWeekday: !!checked,
                  })
                }
              />
              <Label
                htmlFor="observeNearestWeekday"
                className={`text-sm whitespace-nowrap cursor-pointer ${!newHoliday.isRecurring ? "text-gray-400" : ""}`}
                title="If holiday falls on weekend, observe on nearest weekday (Sat→Fri, Sun→Mon)"
              >
                Observed
              </Label>
            </div>
            <Button
              onClick={handleAddHoliday}
              disabled={!newHoliday.name || !newHoliday.date}
              className="w-full sm:w-auto"
            >
              <Plus className="h-4 w-4" />
              <span className="ml-2 sm:hidden">Add Holiday</span>
            </Button>
          </div>

          {/* Holidays List */}
          {isLoadingHolidays ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : displayHolidays.length > 0 ? (
            <div className="border rounded-lg divide-y dark:border-gray-700 dark:divide-gray-700">
              {displayHolidays.map((holiday) => {
                const isPending = holiday.isPending;
                const isRemoving = holiday.isRemoving;

                return (
                  <div
                    key={holiday.id}
                    className={`flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-800 ${
                      isRemoving ? "opacity-50 line-through" : ""
                    } ${isPending ? "bg-green-50 dark:bg-green-950/30" : ""}`}
                  >
                    <div className="flex items-center gap-3">
                      <Calendar
                        className={`h-4 w-4 ${isPending ? "text-green-500" : "text-gray-400"}`}
                      />
                      <div>
                        <p
                          className={`font-medium ${isPending ? "text-green-700 dark:text-green-300" : "text-gray-900 dark:text-white"}`}
                        >
                          {holiday.name}
                          {isPending && (
                            <span className="ml-2 text-xs bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-1.5 py-0.5 rounded">
                              New
                            </span>
                          )}
                          {isRemoving && (
                            <span className="ml-2 text-xs bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 px-1.5 py-0.5 rounded">
                              Will be removed
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {holiday.date}
                          {holiday.isRecurring && (
                            <span className="ml-2 text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-1.5 py-0.5 rounded">
                              Yearly
                            </span>
                          )}
                          {holiday.observeNearestWeekday && (
                            <span
                              className="ml-1 text-xs bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 px-1.5 py-0.5 rounded"
                              title="If holiday falls on weekend, observed on nearest weekday"
                            >
                              Observed
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    {isPending ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          // Find the index in toAdd array
                          const pendingIndex = pendingHolidayChanges.toAdd.findIndex(
                            (h) =>
                              h.name === holiday.name && h.date === holiday.date
                          );
                          if (pendingIndex !== -1) {
                            removePendingHoliday(pendingIndex);
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    ) : isRemoving ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => unmarkHolidayForRemoval(holiday.id)}
                        title="Undo removal"
                      >
                        <Undo2 className="h-4 w-4 text-blue-500" />
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => markHolidayForRemoval(holiday.id)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No holidays configured</p>
              <p className="text-sm">
                Add holidays to exclude them from SLA calculations
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
